import { PaletteOptions } from '@material-ui/core/styles/createPalette';
import { TypographyOptions } from '@material-ui/core/styles/createTypography';
import { Overrides } from '@material-ui/core/styles/overrides';
import { ZIndexOptions } from '@material-ui/core/styles/zIndex';

import { FDSColors } from '@fountain/fds-tokens';
import {
  CustomBackgroundOptions,
  CustomBordersOptions,
  CustomHeightsOptions,
  CustomShadowsOptions,
  CustomWidthsOptions,
  Misc,
  NavigationOptions,
  SuperAdminOptions,
} from 'src/types';

import { boldFamily, bookFamily, CustomColors, mediumFamily, SpecialColors } from './theme.constants';

// custom fountain theme options
export const customBackground: CustomBackgroundOptions = {
  grayGradient: `linear-gradient(0deg, ${FDSColors.gray100} 0%, ${FDSColors.white} 100%)`,
};

export const customBorders: CustomBordersOptions = {
  dropdown: `solid 1px rgba(19, 30, 64, 0.12)`,
  error: `solid 1px ${FDSColors.red400}`,
  gray200: `solid 1px ${FDSColors.gray200}`,
  gray300: `solid 1px ${FDSColors.gray300}`,
  gray400: `solid 1px ${FDSColors.gray400}`,
  primary: `solid 1px ${FDSColors.blue400}`,
};

export const customHeights: CustomHeightsOptions = {
  documentPreviewModal: 1024,
  regularButton: 40,
  regularIcon: 32,
  searchDropdownInput: 56,
  smallButton: 32,
};

// overriding shadows in config requires 25 elevations
export const customShadows: CustomShadowsOptions = {
  card: `0px 1px 3px 0px ${FDSColors.gray200}`,
  chooser: `0px 8px 40px 0px ${FDSColors.gray200}`,
  dropdown: `0 4px 12px rgba(0, 0, 0, 0.175)`,
  error3: `0px 0px 0px 3px ${FDSColors.red100}`,
  footer: `0 8px 16px rgba(19, 30, 64, 0.16)`,
  gray300: `0px 10px 16px ${FDSColors.gray300}, 0px 2px 6px ${FDSColors.gray200}, inset 0px -4px 6px ${FDSColors.gray100}`,
  message: `0px 1px 2px ${FDSColors.gray100}`,
  pageCard: `0px 24px 48px ${FDSColors.gray200}`,
  primary3: `0px 0px 0px 3px ${FDSColors.blue100}`,
  selectDropdown: `0px 1px 2px ${FDSColors.gray100}`,
  shadow1: `0px 1px 2px rgba(0,0,0,0.1)`,
  shadow2: `0px 2px 6px rgba(0, 0, 0, 0.1)`,
  shadow3: `0px 5px 12px rgba(0, 0, 0, 0.1)`,
  shadow4: `0 16px 32px rgba(0, 0, 0, 0.15)`,
  tooltip: `inset -2px 0px 0px 0px ${FDSColors.blue400}, 0px 4px 16px 0px ${FDSColors.gray200}`,
  topShadow: `0 -5px 5px ${FDSColors.gray200}`,
};

export const customWidths: CustomWidthsOptions = {
  appliedColumnWidth: 110,
  archivedReasonColumnWidth: 190,
  customLabelColumnWidth: 240,
  dateColumnWidth: 100,
  dropdownWidth: 200,
  emailColumnWidth: 180,
  idleColumnWidth: 100,
  landedColumnWidth: 110,
  mdFooterMaxWidth: 488,
  // ApplicantTableV2 columns
  nameColumnWidth: 180,
  noteColumnWidth: 240,
  openingColumnWidth: 160,
  phoneColumnWidth: 160,
  progressColumnWidth: 120,
  referredUserColumnWidth: 40,
  regularButton: 124,
  regularIcon: 32,
  smallButton: 120,
  smallMenuMaxWidth: 254,
  smallModalMaxWidth: 270,
  stageColumnWidth: 180,
  statusColumnWidth: 448,
  unreadMessagesCountColumnWidth: 48,
  utmSourceColumnWidth: 110,
};

export const misc: Misc = {
  borderRadius: 5,
  marginDesktop: 32,
  marginMobile: 16,
  mobileBreakpoint: 600,
};

export const navigation: NavigationOptions = {
  appBar: {
    height: 72,
  },
  proBar: {
    height: 40,
  },
  sideBar: {
    width: 240,
  },
};

export const spacing = 8;

export const superAdmin: SuperAdminOptions = {
  collapsed: {
    left: 88,
  },
  expanded: {
    left: 5,
  },
};

export const palette: PaletteOptions = {
  action: {
    hover: FDSColors.gray100,
  },
  // TODO: lets make this more generic so we can reuse these, and make sure these line up with the new color schemes
  alertTypesBackgroundColors: {
    danger: FDSColors.red100,
    informational: FDSColors.blue100,
    success: FDSColors.green100,
    warning: FDSColors.orange100,
  },
  background: {
    default: FDSColors.gray100,
    paper: FDSColors.white,
  },
  common: {
    black: FDSColors.black,
    white: FDSColors.white,

    // custom fountain color options
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    amber50: CustomColors.amber50,
    amber800: CustomColors.amber800,
    hireGoTint: CustomColors.hireGoTint,
    hireGoText: CustomColors.hireGoText,
    blue100: FDSColors.blue100,
    blue200: FDSColors.blue200,
    blue400: FDSColors.blue400,
    blue50: FDSColors.blue50,
    blue500: FDSColors.blue500,
    blue600: FDSColors.blue600,
    blueAppBarBackground: FDSColors.gray800,
    blueTooltip: FDSColors.gray800,
    // FDS carries three light blue rungs and Ripple's brand ramp carries two,
    // so `blue100` and `blue200` each split. These roles name the halves while
    // all four still hold their FDS value, so the repoint that follows changes
    // a token per role rather than a value per call site.
    //
    // The brand tint is one rung. The rung above it is spent only on a hover
    // that sits on top of that tint, which is why a selected row with no hover
    // and a hover with no tint under it both read `brandTint`. Ripple spends
    // its rungs the same way: `menu-item`, `select` and `card` all paint
    // selected at `brand-50`, and nothing there steps between the two tints.
    //
    // Two pairs land on one Ripple rung: `brandSelectedHover` with
    // `brandTintHover`, and `brandIconTint` with `brandTint`. Each pair stays
    // two roles because the two halves are different decisions — a hover over
    // a selected row and a hover over a tint chip, a disc behind an icon and a
    // resting chip — and the next person to revisit one should not have to
    // sort the other's call sites out of the same grep.
    brandIconTint: FDSColors.blue100,
    brandSelectedHover: FDSColors.blue100,
    brandTint: FDSColors.blue100,
    brandTintBorder: FDSColors.blue200,
    brandTintEdge: FDSColors.blue100,
    brandTintHover: FDSColors.blue200,
    danger: FDSColors.red600,
    dangerHover: FDSColors.red600,
    dangerText: FDSColors.red800,
    error: FDSColors.red400,
    gray100: FDSColors.gray100,
    gray200: FDSColors.gray200,
    gray300: FDSColors.gray300,
    gray400: FDSColors.gray400,
    gray500: FDSColors.gray500,
    gray600: FDSColors.gray600,
    gray700: FDSColors.gray700,
    gray800: FDSColors.gray800,
    green100: FDSColors.green100,
    green400: FDSColors.green400,
    green600: FDSColors.green600,
    green800: FDSColors.green800,
    iconGreen100: SpecialColors.Icon.GREEN_100,
    iconPink100: SpecialColors.Icon.PINK_100,
    iconPurple100: SpecialColors.Icon.PURPLE_100,
    iconPurple200: SpecialColors.Icon.PURPLE_200,

    // FDS blue does two jobs: it is Hire's brand, and it is information.
    // Ripple keeps those apart, and a theme key cannot point at two tokens —
    // `blue100` and `blue400` are each read by both halves. These roles carry
    // the informational half so the brand half can bind its own token without
    // dragging every toast, banner and info glyph along with it.
    //
    // `infoMark` is named for the contrast it owes rather than the value it
    // holds: it paints a glyph or a stripe and never text. Its Ripple target
    // clears SC 1.4.11's 3:1 non-text minimum and fails AA's 4.5:1, so a text
    // call site must not reach for it.
    infoMark: FDSColors.blue400,
    infoTint: FDSColors.blue100,
    orange100: FDSColors.orange100,
    orange200: FDSColors.orange200,
    orange400: FDSColors.orange400,
    orange400Dark: FDSColors.orange400,
    orange600: FDSColors.orange600,
    orange800: FDSColors.orange800,
    red100: FDSColors.red100,
    red400: FDSColors.red400,
    red600: FDSColors.red600,
    red800: FDSColors.red800,
    success: FDSColors.green600,
    successHover: FDSColors.green400,
    warningBorder: FDSColors.orange200,
    warningFill: FDSColors.orange600,

    // `orange600` is the icon weight: 3.11:1 on white and 2.83:1 on
    // `orange100`, so it clears the 3:1 non-text minimum on white and fails
    // AA's 4.5:1 on both.
    // This role is named for the contrast it owes, not the value it holds, and
    // must resolve to a text weight.
    warningText: FDSColors.orange600,
  },
  error: {
    contrastText: FDSColors.white,
    // Declared for the reason `primary` states below. The applicant drawer's
    // attention disc is the only reader: `dark` is its glyph and `light` its
    // fill. Material-UI 4.9.14 reads neither rung — `Button` carries no error
    // colour, and nothing in core or lab touches them.
    dark: FDSColors.red600,
    light: FDSColors.red100,
    main: FDSColors.red400,
  },
  // No `info` group, and this is a gap rather than a tidy omission. Material-UI
  // fills one from its own `#2196f3`, and lab's `Alert severity="info"` derives
  // its icon, tint and text from it — five recruiter-ui call sites render one,
  // so that stock blue is on screen today.
  //
  // It still cannot be declared on Ripple's blue ramp from this file:
  // recruiter-ui's bridge binds every FDS blue to the brand, and a value under
  // `palette.<group>` is substituted by hex alone — the role names that hold
  // `blue-600` and `blue-50` apply only under `palette.common`. Declaring it
  // here would paint those five Alerts brand indigo, which is a different wrong
  // answer. Reaching the blue ramp needs a literal Ripple value here or a
  // bridge that keys roles on a path.
  primary: {
    contrastText: FDSColors.white,
    // Material-UI derives `light` and `dark` from `main` while it creates the
    // theme, before anything downstream can rewrite a value. Derived rungs
    // therefore keep whatever hue `main` held at creation, and a consumer that
    // repoints `main` alone ends up resting on one ramp and hovering onto
    // another. Declaring both keeps the whole group on one ramp.
    //
    // `dark` is the hover rung: Material-UI's contained `Button`, `Fab`,
    // `Chip` and `ButtonGroup` all read it, as do the scorecard's selected
    // buttons and the job-description link. `light` has no reader in
    // Material-UI or here, and the ramp carries no rung between the tint and
    // `main`, so it takes the tint.
    dark: FDSColors.blue500,
    light: FDSColors.blue50,
    main: FDSColors.blue400,
  },
  secondary: {
    contrastText: FDSColors.white,
    // `dark` is the hover rung Material-UI's contained `Button`, `Fab`, `Chip`
    // and `ButtonGroup` read. `light` has no reader in Material-UI or here.
    dark: FDSColors.gray700,
    light: FDSColors.gray400,
    main: FDSColors.gray600,
  },
  // Material-UI fills `success` and `warning` from its own palette unless they
  // are declared, so every reader paints Material-UI's green and amber — values
  // on no Fountain ramp, which no migration keyed on FDS hexes could ever find.
  //
  // `warning.main` resolves to Ripple's `warning`, 2.62:1 on white. Three
  // things read it: an 8px status dot, a 10px one, and lab's `Alert
  // severity="warning"` icon, which sits on the tint the Alert derives from
  // this same value and so reads 2.38:1. Each of the three is captioned, so the
  // colour reinforces a sentence rather than carrying the meaning alone. The
  // Alert's own text is `darken(main, 0.6)` at 9.56:1, so nothing a reader has
  // to parse depends on the icon. A tinted fill under a dark glyph is the
  // pattern for anything larger.
  success: {
    contrastText: FDSColors.white,
    dark: FDSColors.green800,
    light: FDSColors.green100,
    main: FDSColors.green600,
  },
  text: {
    disabled: FDSColors.gray500,
    hint: FDSColors.gray400,
    primary: FDSColors.gray800,
    secondary: FDSColors.gray600,
  },

  tonalOffset: 0.1,
  warning: {
    // Not white: white on `warning` is 2.62:1. The darkest neutral reads 6.81:1
    // on it.
    contrastText: FDSColors.gray800,
    dark: FDSColors.orange800,
    light: FDSColors.orange100,
    main: FDSColors.orange400,
  },
  // custom fountain palette option
  // eslint-disable-next-line sort-keys-fix/sort-keys-fix
  avatar: {
    abc: SpecialColors.Avatar.PURPLE,
    def: SpecialColors.Avatar.BLUE_1,
    ghi: SpecialColors.Avatar.ORANGE_2,
    jkl: SpecialColors.Avatar.GREEN_1,
    mno: SpecialColors.Avatar.GREEN_2,
    pqr: SpecialColors.Avatar.TEAL,
    stu: SpecialColors.Avatar.ORANGE,
    vwx: SpecialColors.Avatar.PINK,
    yz: SpecialColors.Avatar.RED,
  },
  icon: {
    default: FDSColors.gray500,
    primary: FDSColors.blue400,
    white: FDSColors.white,
  },
  statusLabel: {
    HoverColor: {
      ACTION: SpecialColors.StatusLabel.HoverColor.ACTION,
      INCOMPLETE: SpecialColors.StatusLabel.HoverColor.INCOMPLETE,
      IN_PROGRESS: SpecialColors.StatusLabel.HoverColor.IN_PROGRESS,
      PARTNER: SpecialColors.StatusLabel.HoverColor.PARTNER,
      SUCCESS: SpecialColors.StatusLabel.HoverColor.SUCCESS,
      WARNING: SpecialColors.StatusLabel.HoverColor.WARNING,
    },
    IconColor: {
      ACTION: SpecialColors.StatusLabel.IconColor.ACTION,
      INCOMPLETE: SpecialColors.StatusLabel.IconColor.INCOMPLETE,
      IN_PROGRESS: SpecialColors.StatusLabel.IconColor.IN_PROGRESS,
      PARTNER: SpecialColors.StatusLabel.IconColor.PARTNER,
      SUCCESS: SpecialColors.StatusLabel.IconColor.SUCCESS,
      WARNING: SpecialColors.StatusLabel.IconColor.WARNING,
    },
    LabelColor: {
      ACTION: SpecialColors.StatusLabel.LabelColor.ACTION,
      INCOMPLETE: SpecialColors.StatusLabel.LabelColor.INCOMPLETE,
      IN_PROGRESS: SpecialColors.StatusLabel.LabelColor.IN_PROGRESS,
      PARTNER: SpecialColors.StatusLabel.LabelColor.PARTNER,
      SUCCESS: SpecialColors.StatusLabel.LabelColor.SUCCESS,
      WARNING: SpecialColors.StatusLabel.LabelColor.WARNING,
    },
  },
};

export const typography: TypographyOptions = {
  body1: {
    fontFamily: bookFamily,
    fontSize: '1rem',
  },
  body2: {
    color: FDSColors.gray600,
    fontFamily: bookFamily,
    fontSize: '0.875rem',
    lineHeight: '1rem',
  },
  button: {
    color: FDSColors.white,
    fontSize: '1rem',
    fontWeight: 500,
    textTransform: 'none',
  },
  caption: {
    fontFamily: bookFamily,
    fontSize: '0.875rem',
  },
  fontFamily: bookFamily,
  h1: {
    color: FDSColors.gray800,
    fontFamily: mediumFamily,
    fontSize: '2.5rem',
  },
  h2: {
    color: FDSColors.gray800,
    fontFamily: mediumFamily,
    fontSize: '1.5rem',
    fontWeight: 'normal',
    lineHeight: '1.5rem',
  },
  h3: {
    color: FDSColors.gray800,
    fontFamily: mediumFamily,
    fontSize: '1.125rem',
    lineHeight: '1.5rem',
  },
  h4: {
    fontFamily: mediumFamily,
    fontSize: '0.875rem',
    lineHeight: '1rem',
  },
  h5: {
    fontFamily: boldFamily,
    fontSize: '0.75rem',
    lineHeight: '1rem',
  },
  h6: {
    fontFamily: boldFamily,
    fontSize: '0.75rem',
    lineHeight: '1rem',
  },
  overline: {
    fontFamily: bookFamily,
    fontSize: '0.75rem',
    lineHeight: '1rem',
    textTransform: 'none',
  },
  subtitle1: {
    fontFamily: bookFamily,
    fontSize: '1.5rem',
  },
  subtitle2: {
    fontFamily: bookFamily,
    fontSize: '1.125rem',
  },
};

// modified ZIndexOptions
export const zIndex: ZIndexOptions = {
  appBar: 600,
  customModal: 850,
  drawer: 700,
  dropdownMenu: 801,
  floatingLink: 8,
  loader: 1600,
  messageTemplatesMenu: 7,
  mobileCloseIcon: 1000,
  mobileDropdownMenu: 5,
  mobileStepper: 500,
  modal: 800,
  previewFullscreen: 1000,
  snackbar: 900,
  speedDial: 550,
  splash: 650,
  tooltip: 1000,
};

// mui theme options
export const overrides: Overrides = {
  MuiBackdrop: {
    root: {
      backgroundColor: 'rgba(0,0,0,0.1)',
    },
  },
  MuiButton: {
    root: {
      '&:hover': {
        backgroundColor: FDSColors.gray100,
      },
    },
  },
  MuiCheckbox: {
    indeterminate: {
      '&:not(.Mui-disabled)': {
        '& svg': {
          backgroundColor: FDSColors.blue400,
          boxShadow: 'none',
          fill: FDSColors.white,
        },
        '&:hover': {
          '& svg': {
            backgroundColor: FDSColors.blue600,
          },
        },
      },
    },
    root: {
      '& svg': {
        borderRadius: 3,
        boxShadow: `inset 0 0 0 1px ${FDSColors.gray400}`,
        height: 16,
        transition: 'box-shadow 0.3s ease',
        width: 16,
      },
      '&.Mui-checked': {
        '&:not(.Mui-disabled)': {
          '& svg': {
            backgroundColor: FDSColors.blue400,
            boxShadow: 'none',
          },
          '&:hover': {
            '& svg': {
              backgroundColor: FDSColors.blue600,
            },
            backgroundColor: 'transparent',
          },
        },
      },
      '&.Mui-disabled': {
        '& svg': {
          backgroundColor: FDSColors.gray300,
          boxShadow: 'none',
        },
      },
      '&.Mui-focusVisible': {
        '& svg': {
          outline: '2px auto rgba(19,124,189,.6)',
          outlineOffset: 2,
        },
      },
      '&.error': {
        '& svg': {
          boxShadow: `inset 0 0 0 1px ${FDSColors.red400}`,
        },
      },
      '&:hover': {
        backgroundColor: 'transparent',
      },
      padding: '8px',
    },
  },
  MuiDivider: {
    root: {
      backgroundColor: FDSColors.gray300,
    },
  },
  MuiInputBase: {
    input: {
      fontSize: '0.875rem',
    },
  },
  MuiListItemText: {
    primary: {
      color: FDSColors.gray800,
      fontSize: '0.875rem',
    },
    secondary: {
      color: FDSColors.gray600,
      fontSize: '0.875rem',
    },
  },
  MuiMenuItem: {
    root: {
      boxSizing: 'content-box',
      fontSize: 14,
      minHeight: 36,
    },
  },
  // `theme.props.tsx` sets `disableRipple: true` for every Radio, and unlike
  // `MuiCheckbox` above, nothing here styled `.Mui-focusVisible` — so a plain
  // `<Radio>` (recruiter-ui's own, unstyled) took keyboard focus with no
  // visible indicator at all. Scoped to `& svg` rather than `root`: a raw
  // Radio renders two stacked SVGs for the ring and the dot (20x20 at
  // `size="small"`, 24x24 at the default), both sharing one bounding box, so
  // this reads as a single ring sized to the glyph — not the much larger
  // padded hit-target `root` covers.
  MuiRadio: {
    root: {
      '&.Mui-focusVisible': {
        '& svg': {
          outline: `2px auto ${FDSColors.blue400}`,
          outlineOffset: 2,
        },
      },
    },
  },
  MuiSwitch: {
    colorSecondary: {
      '&.Mui-checked': {
        '&:not(.Mui-disabled)': {
          '& + .MuiSwitch-track': {
            backgroundColor: FDSColors.blue400,
            opacity: 1,
          },
        },
        color: FDSColors.white,
      },
    },
    root: {
      '&$checked': {
        transform: 'translateX(18px)',
      },
      display: 'flex',
      height: 16,
      padding: 0,
      width: 28,
    },
    switchBase: {
      '&.Mui-checked': {
        '&:hover': {
          backgroundColor: 'transparent',
        },
        color: FDSColors.white,
        transform: 'translateX(12px)',
      },
      '&:hover': {
        backgroundColor: 'transparent',
      },
      color: FDSColors.white,
      padding: 2,
    },
    thumb: {
      boxShadow: 'none',
      height: 12,
      width: 12,
    },
    track: {
      backgroundColor: FDSColors.gray400,
      borderRadius: 8,
      opacity: 1,
    },
  },
  MuiTableCell: {
    root: {
      borderBottom: 'none',
    },
  },
  MuiTooltip: {
    popper: {
      '&[x-placement*="bottom"] .fountain-tooltip-arrow': {
        height: '1em',
        left: 0,
        marginTop: '-0.5rem',
        top: 0,
        width: '2em',

        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        '&::before': {
          borderColor: `transparent transparent ${FDSColors.gray800} transparent`,
          borderWidth: '0 1em 1em 1em',
        },

        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        '&.fountain-tooltip-dense': {
          marginTop: '-0.25rem !important',
        },
      },
      '&[x-placement*="left"] .fountain-tooltip-arrow': {
        height: '2em',
        marginRight: '-0.5rem',
        right: 0,
        width: '1em',

        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        '&::before': {
          borderColor: `transparent transparent transparent ${FDSColors.gray800}`,
          borderWidth: '1em 0 1em 1em',
        },

        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        '&.fountain-tooltip-dense': {
          marginRight: '-0.25rem !important',
        },
      },

      '&[x-placement*="right"] .fountain-tooltip-arrow': {
        height: '2em',
        left: 0,
        marginLeft: '-0.5rem',
        width: '1em',

        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        '&::before': {
          borderColor: `transparent ${FDSColors.gray800} transparent transparent`,
          borderWidth: '1em 1em 1em 0',
        },

        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        '&.fountain-tooltip-dense': {
          marginLeft: '-0.25rem !important',
        },
      },

      '&[x-placement*="top"] .fountain-tooltip-arrow': {
        bottom: 0,
        height: '1em',
        left: 0,
        marginBottom: '-0.5rem',
        width: '2em',

        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        '&::before': {
          borderColor: `${FDSColors.gray800} transparent transparent transparent`,
          borderWidth: '1em 1em 0 1em',
        },

        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        '&.fountain-tooltip-dense': {
          marginBottom: '-0.25rem !important',
        },
      },
    },
    tooltip: {
      backgroundColor: FDSColors.gray800,
      borderRadius: `${misc.borderRadius}px`,
      padding: '0.5rem',
      position: 'relative',

      // eslint-disable-next-line sort-keys-fix/sort-keys-fix
      '&.fountain-tooltip-dense': {
        padding: '0.5rem 1rem',
      },

      // eslint-disable-next-line sort-keys-fix/sort-keys-fix
      '& .fountain-tooltip-arrow': {
        '&::before': {
          borderStyle: 'solid',
          content: '""',
          display: 'block',
          height: 0,
          margin: 'auto',
          width: 0,
        },
        fontSize: '0.5rem',
        position: 'absolute',

        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        '&.fountain-tooltip-dense': {
          fontSize: '0.25rem',
        },
      },

      // eslint-disable-next-line sort-keys-fix/sort-keys-fix
      '& .fountain-tooltip-text': {
        color: FDSColors.gray100,
        fontSize: (typography && typography.body1 && typography.body1.fontSize) || 'initial',
        whiteSpace: 'pre-line',

        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        '&.fountain-tooltip-dense': {
          fontSize: (typography && typography.overline && typography.overline.fontSize) || 'initial',
        },
      },
    },
    tooltipPlacementBottom: {
      marginBottom: '0.5rem',

      // eslint-disable-next-line sort-keys-fix/sort-keys-fix
      '&.fountain-tooltip-more-margin': {
        marginBottom: '1.25rem',
      },

      '@media(min-width: 600px)': {
        marginTop: '0.5rem',
      },
    },
    tooltipPlacementLeft: {
      marginLeft: '0.5rem',

      // eslint-disable-next-line sort-keys-fix/sort-keys-fix
      '&.fountain-tooltip-more-margin': {
        marginLeft: '1.25rem',
      },
    },
    tooltipPlacementRight: {
      marginRight: '0.5rem',

      // eslint-disable-next-line sort-keys-fix/sort-keys-fix
      '&.fountain-tooltip-more-margin': {
        marginRight: '1.25rem',
      },
    },
    tooltipPlacementTop: {
      marginTop: '0.5rem',

      // eslint-disable-next-line sort-keys-fix/sort-keys-fix
      '&.fountain-tooltip-more-margin': {
        marginTop: '1.25rem',
      },

      '@media(min-width: 600px)': {
        marginBottom: '0.5rem',
      },
    },
  },
};
